import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useGlobalContext } from 'GlobalContext';
import { useSettings } from 'services/settings/SettingsContext';
import { EmailSendingAddress, EmailTemplate } from '../../../views/email/EmailTypes';
import { GroupType } from '../../../views/users/UserTypes';
import { sendToOptionsMap } from './emailTemplateFormOptions';
import { acceptedFileTypes } from '../../../views/attachment/AttachmentTypes';
import MultiSelect from 'components/forms/basefields/MultiSelect';
import TextareaInput, { TextareaInputHandle } from 'components/forms/basefields/TextareaInput';
import Dropdown from 'components/forms/basefields/Dropdown';
import AttachmentSelect from 'components/forms/basefields/AttachmentSelect';
import CheckboxCheck from 'assets/CheckboxCheck.svg';
import { useAuthContext } from 'services/authentication/AuthenticationContext';
import EmailTemplateTagSelector from './templates/EmailTemplateTagSelector';

interface EmailTemplateFormMessageFieldsProps {
    data: EmailTemplate | null;
    loading: 'idle' | 'show-loader' | 'success';
    groups: GroupType[];
    sendingAddresses: EmailSendingAddress[];
    filteredSendingAddresses: EmailSendingAddress[] | [];
    selectedSendTo: string[];
    setData: React.Dispatch<React.SetStateAction<EmailTemplate | null>>;
    setSelectedSendTo: React.Dispatch<React.SetStateAction<string[]>>;
}

const EmailTemplateFormMessageFields: React.FC<EmailTemplateFormMessageFieldsProps> = ({
    data, loading, groups, sendingAddresses, filteredSendingAddresses, selectedSendTo,
    setData, setSelectedSendTo,
}) => {
    const { t } = useTranslation();
    const { errorMessages } = useGlobalContext();
    const { activeFeatures } = useAuthContext();
    const { environmentSettings } = useSettings();
    const subjectInputRef = useRef<HTMLInputElement>(null);
    const bodyInputRef = useRef<TextareaInputHandle>(null);
    const [activeTemplateField, setActiveTemplateField] = useState<'subject' | 'body'>('body');

    // Filter the send to options on the selected connected feature
    const filteredSendToOptions = data?.connected_feature ? sendToOptionsMap[data.connected_feature] || [] : [];

    // Handle sending address change
    const handleSendingAddressChange = (selectedValue: string) => {
        const sendingAddressId = parseFloat(selectedValue);

        // Get the sending address object
        const selectedAddress = sendingAddresses.find(address => address.id === sendingAddressId);
        if (selectedAddress) {
            setData(prev => ({
                ...prev!,
                sending_address: sendingAddressId,
                custom_domain: selectedAddress.domain,
                from_email: selectedAddress.email,
                reply_to: selectedAddress.reply_email,
            }));
        }
    };

    // Insert an email tag in the subject
    const insertTagInSubject = (tag: string) => {
        const input = subjectInputRef.current;
        const currentValue = data?.subject ?? '';

        if (input) {
            const start = input.selectionStart ?? currentValue.length;
            const end = input.selectionEnd ?? currentValue.length;
            const updatedValue = `${currentValue.slice(0, start)}${tag}${currentValue.slice(end)}`;

            // Update the data
            setData(prev => ({ ...prev!, subject: updatedValue }));

            requestAnimationFrame(() => {
                input.focus();
                input.setSelectionRange(start + tag.length, start + tag.length);
            });
        } else {
            setData(prev => ({
                ...prev!,
                subject: `${prev?.subject ?? ''}${tag}`
            }));
        }
    };

    // Insert an email tag in the body
    const insertTagInBody = (tag: string) => {
        bodyInputRef.current?.insertAtCursor(tag);
    };

    // Insert a tag either in the subject of body field
    const insertTag = (tag: string) => {
        if (activeTemplateField === 'subject') {
            insertTagInSubject(tag);
        } else {
            insertTagInBody(tag);
        }
    };

    return (
        <>
            <div className='card'>
                <h6 className='template-fields-header'>
                    {t('email.email_template.compose_message_title')}
                </h6>
                {loading === 'show-loader' ? (
                    <div className='inline-form-loader'>
                        <div className="loader"></div>
                    </div>
                ) : loading === 'success' ? (
                    data?.connected_feature !== null ? (
                        <>
                            <div className='edit-mode'>
                                <label>
                                    {t('email.email_template.send_to_label')}
                                </label>
                                <MultiSelect<{ value: string, name: string }>
                                    options={filteredSendToOptions}
                                    id='send_to'
                                    name='send_to'
                                    disabled_selected={t('email.email_template.send_to_disabled_selected')}
                                    selectedOption={filteredSendToOptions.filter(option => selectedSendTo.includes(option.value))}
                                    onChange={(changedValue) => {
                                        setSelectedSendTo(prev => {
                                            let updatedValues: string[];
                                            if (prev.includes(changedValue)) {
                                                updatedValues = prev.filter(v => v !== changedValue);
                                            } else {
                                                updatedValues = [...prev, changedValue];
                                            }
                                            setData(prevData => ({ ...prevData!, send_to: updatedValues }));
                                            return updatedValues;
                                        });
                                    }}
                                    selectionFormat={(option) => `${option.name}`}
                                    optionFormat={(option) => `${option.name}`}
                                    showSearch={false}
                                    allowNoneOption={false}     
                                    isInvalid={errorMessages['send_to']}                       
                                />
                                {errorMessages['send_to'] && (
                                    <div className='error-message'>
                                        {t(errorMessages['send_to'])}
                                    </div>
                                )}
                                {data?.connected_feature === 'jobs' && (
                                    <p className='helper-text'>
                                        {t('email.email_template.send_to_helperText_job')}
                                    </p>
                                )}
                                {data?.connected_feature === 'invoices' && data?.send_to?.includes('client_invoice_address') && (
                                    <p className='helper-text'>
                                        {t('email.email_template.send_to_helperText_invoice')}
                                    </p>
                                )}
                            </div>
                            {data?.send_to?.includes('user_group') && activeFeatures.includes('groups') && (
                                <div className='edit-mode'>
                                    <label>
                                        {t('email.email_template.user_group_label')}
                                    </label>
                                    <Dropdown<GroupType>
                                        options={groups?.filter(group => !group.deleted) || []}
                                        id='send_to_user_group'
                                        name='send_to_user_group'
                                        disabled_selected={t('email.email_template.send_to_user_group_disabled_selected')}
                                        selectedOption={groups.find(group => data?.send_to_user_group.includes(group.id))}
                                        onChange={(selectedValue) => setData(prev => ({...prev!, send_to_user_group: [parseFloat(selectedValue)]}))}
                                        selectionFormat={(option) => `${option.name}`}
                                        optionFormat='name'
                                        showSearch={false}
                                        allowNoneOption={false}
                                    />
                                    <p className='helper-text'>
                                        {t('email.email_template.user_group_helperText')}
                                    </p>
                                </div>
                            )}
                            {data?.send_to?.includes('specific_email') && (
                                <div className='edit-mode'>
                                    <label>
                                        {t('email.email_template.specific_email_label')}
                                    </label>
                                    <input
                                        type="text"
                                        id='send_to_specific_email'
                                        name='send_to_specific_email'
                                        placeholder={t('email.email_template.specific_email_placeholder')}
                                        value={data?.send_to_specific_email ?? ''}
                                        onChange={(e) => setData(prev => ({...prev!, send_to_specific_email: e.target.value}))}
                                        className={`text ${(errorMessages['send_to_specific_email']) ? 'is-invalid' : ''}`}
                                        autoFocus={false}
                                    />
                                    {errorMessages['send_to_specific_email'] && (
                                        <div className='error-message'>
                                            {t(errorMessages['send_to_specific_email'])}
                                        </div>
                                    )}
                                </div>
                            )}
                            <div className='edit-mode'>
                                <label>
                                    {t('email.email_template.default_bcc_recipient_label')}
                                </label>
                                <input
                                    type="text"
                                    id='default_bcc_recipient'
                                    name='default_bcc_recipient'
                                    placeholder={t('email.email_template.default_bcc_recipient_placeholder')}
                                    value={data?.default_bcc_recipient ?? ''}
                                    onChange={(e) => setData(prev => ({...prev!, default_bcc_recipient: e.target.value}))}
                                    className={`text ${(errorMessages['default_bcc_recipient']) ? 'is-invalid' : ''}`}
                                    autoFocus={false}
                                />
                                {errorMessages['default_bcc_recipient'] && (
                                    <div className='error-message'>
                                        {t(errorMessages['default_bcc_recipient'])}
                                    </div>
                                )}
                            </div>
                            {environmentSettings.email_domain_method === 'custom' && filteredSendingAddresses.length > 0 ? (
                                <div className='edit-mode'>
                                    <label>
                                        {t('email.email_template.from_email_label')}
                                    </label>
                                    <Dropdown<EmailSendingAddress>
                                        options={filteredSendingAddresses || []}
                                        id='sending_address'
                                        name='sending_address'
                                        disabled_selected={t('email.email_sending_address.select_sending_address_disabled_selected')}
                                        selectedOption={sendingAddresses.find(address => data?.sending_address === address.id)}
                                        onChange={(selectedValue) => handleSendingAddressChange(selectedValue)}
                                        selectionFormat={(option) => option.email}
                                        optionFormat={(option) => option.email}
                                        showSearch={false}
                                        allowNoneOption={false}
                                        isInvalid={errorMessages['sending_address']}
                                    />
                                    {errorMessages['sending_address'] && (
                                        <div className='error-message'>
                                            {t(errorMessages['sending_address'])}
                                        </div>
                                    )}
                                    {data?.reply_to && (
                                        <p className='helper-text'>
                                            {t('email.email_template.reply_to_example', { reply_to: data.reply_to })}
                                        </p>
                                    )}
                                    {errorMessages['from_email'] && (
                                        <div className='error-message'>
                                            {t(errorMessages['from_email'])}
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div className='edit-mode'>
                                    <div className='fake-input'>
                                        <label>
                                            {t('email.email_template.from_email_label')}
                                        </label>
                                        <div>
                                            {data?.from_email}
                                        </div>
                                    </div>
                                </div>
                            )}
                            <div className='edit-mode'>
                                <label>
                                    {t('email.email_template.subject_label')}
                                </label>
                                <input
                                    ref={subjectInputRef}
                                    type="text"
                                    id='subject'
                                    name='subject'
                                    placeholder={t('email.email_template.subject_placeholder')}
                                    value={data?.subject ?? ''}
                                    onFocus={() => setActiveTemplateField('subject')}
                                    onChange={(e) => setData(prev => ({...prev!, subject: e.target.value}))}
                                    className={`text ${(errorMessages['subject']) ? 'is-invalid' : ''}`}
                                    autoFocus={false}
                                />
                                {errorMessages['subject'] && (
                                    <div className='error-message'>
                                        {t(errorMessages['subject'])}
                                    </div>
                                )}
                            </div>
                            <div className='edit-mode'>
                                <label>
                                    {t('email.email_template.body_label')}
                                </label>
                                <TextareaInput
                                    ref={bodyInputRef}
                                    name='body'
                                    value={data?.body ?? ''}
                                    placeholder='email.email_template.body_placeholder'
                                    rows={8}
                                    enableStyling='simple'
                                    showToolbarInitially={true}
                                    onFocus={() => setActiveTemplateField('body')}
                                    onChange={(value: any) => setData(prev => ({...prev!, body: String(value)}))}
                                    isInvalid={errorMessages['body']} 
                                />
                                {errorMessages['body'] && (
                                    <div className='error-message'>
                                        {t(errorMessages['body'])}
                                    </div>
                                )}
                            </div>
                            <EmailTemplateTagSelector
                                connectedFeature={data?.connected_feature}
                                locale={environmentSettings?.default_locale}
                                onSelect={insertTag}
                            />
                        </>
                    ) : (
                        <div className='no-connected-feature-helper'>
                            <p>{t('email.general.no_connected_feature_selected_helpertext')}</p>
                        </div>
                    )
                ) : null}
            </div>
            {data?.template_type === 'appointment_confirmation_request' && (
                <div className='card'>
                    <h6 className='template-fields-header'>
                        {t('email.email_template.options_title')}
                    </h6>
                    {loading === 'show-loader' ? (
                        <div className='inline-form-loader'>
                            <div className="loader"></div>
                        </div>
                    ) : loading === 'success' ? (
                        <div className='checkbox-item left'>
                            <div className="label-description-container">
                                <label htmlFor='appointment_confirmation_button' className='checkbox-label'>
                                    {t('email.email_template.appointment_confirmation_description')}
                                </label>
                            </div>
                            <div className='checkbox-wrapper'>
                                <input 
                                    type='checkbox'
                                    id='appointment_confirmation_button'
                                    checked={data?.appointment_confirmation_button || undefined}
                                    onChange={(event) => setData(prev => ({...prev!, appointment_confirmation_button: event.target.checked}))}
                                    className='hidden-checkbox' />
                                <label htmlFor='appointment_confirmation_button'
                                    className={`custom-checkbox left ${data?.appointment_confirmation_button === true ? 'checked' : ''}`}>
                                    {data?.appointment_confirmation_button === true && <img src={CheckboxCheck} alt="CheckboxCheck" className='checkbox-check' />}
                                </label>
                            </div>
                        </div>
                    ) : null}
                </div>
            )}
            <div className='card'>
                <h6 className='template-fields-header'>
                    {t('email.email_template.attachments_title')}
                </h6>
                {loading === 'show-loader' ? (
                    <div className='inline-form-loader'>
                        <div className="loader"></div>
                    </div>
                ) : loading === 'success' ? (
                    <>
                        <label>
                            {t('email.email_template.default_attachments_description')}
                        </label>
                        <AttachmentSelect
                            attachments={data?.default_attachments ?? []}
                            addObjectPdf={
                                data?.connected_feature && !['appointments', 'tickets', 'contacts'].includes(data.connected_feature)
                                    ? {
                                        shouldAddObjectPdf: true,
                                        connectedFeature: data?.connected_feature,
                                        addObjectPdfValue: data.add_object_pdf ?? false
                                    }
                                    : undefined
                            }
                            acceptedFileTypes={acceptedFileTypes}
                            mode='settings'
                            onChange={({ attachments, newAttachments, addObjectPdfValue }) => {
                                setData(prev => {
                                    if (!prev) return prev;
                                    return {
                                        ...prev,
                                        default_attachments: attachments,
                                        new_default_attachments: newAttachments,
                                        add_object_pdf: addObjectPdfValue ?? prev.add_object_pdf,
                                    };
                                });
                            }}
                        />
                    </>
                ) : null}
            </div>
        </>
    )
};

export default EmailTemplateFormMessageFields;